import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { AppShell } from '../layout/AppShell'
import { Icon } from '../components/Icon'
import { navigate } from '../router/hash'
import { ApiError } from '../api/auth'
import { createClient, getClient, updateClient } from '../api/clients'
import type { ClientStatus } from '../data/types'
import {
  CODE_STATUS_OPTIONS,
  DISCHARGE_NOTE_OPTIONS,
  DRIVER_OPTIONS,
  ET_SETTING_OPTIONS,
  ETHNICITY_OPTIONS,
  FOOD_PROGRAM_TYPE_OPTIONS,
  MARITAL_OPTIONS,
  MEAL_OPTIONS,
  RACE_OPTIONS,
  ROUTE_OPTIONS,
  SEX_OPTIONS,
  SHIFT_OPTIONS,
  SIGNATURE_FONT_OPTIONS,
  STATUS_OPTIONS,
  US_STATES,
  WAIVER_OPTIONS,
  WEEKDAY_LABELS,
  WEEKDAYS,
} from '../data/addClientForm'

type Props = { currentPath: string; clientId?: string }

type MultiKey =
  | 'code_status'
  | 'cli_schedule'
  | 'cli_waiver'
  | 'cli_schedule_meals'
  | 'cli_tra_schedule'

type FormState = {
  cli_name: string
  cli_client_id: string
  cli_first_name: string
  cli_last_name: string
  cli_middle_name: string
  cli_sex: string
  cli_phone: string
  cli_cell_phone: string
  cli_dob: string
  cli_adm_date: string
  cli_email: string
  cli_security: string
  cli_address: string
  cli_address2: string
  cli_state: string
  cli_city: string
  cli_zip: string
  cli_material_status: string
  cli_guardian: string
  cli_guardian_name: string
  cli_guardian_phone: string
  cli_proxy: string
  cli_proxy_name: string
  cli_proxy_phone: string
  cli_primary_lang: string
  cli_secondary_lang: string
  cli_ethenicity: string
  cli_race: string
  cli_religion: string
  cli_program_name: string
  cli_comments: string
  cli_spouse_significant_name: string
  cli_referral_date: string
  cli_referral_source: string
  ref_organization_name: string
  ref_organization_contact_name: string
  ref_organization_contact_email: string
  ref_organization_contact_phone: string
  cli_transfer_dcc_id: string
  cli_medicare: string
  cli_medicaid: string
  cli_is_waiver: string
  cli_waiver: string[]
  cli_adv_dir_cir: string
  cli_hospital_preference: string
  cli_add_history: string
  cli_mltss: string
  cli_mltss_manager_name: string
  cli_mltss_manager_no: string
  cli_mltss_manager_email: string
  cli_mltss_manager_ext: string
  cli_fac_schedule_daily: string
  cli_schedule: string[]
  cli_schedule_timing: string
  cli_schedule_shift: string
  shiftstarttime: string
  shiftendtime: string
  cli_tra_schedule_daily: string
  cli_et_setting: string
  cli_tra_schedule: string[]
  cli_third_party_transportation: string
  cli_third_party_transportation_reason: string
  cli_trans_route_id: string
  cli_pick_seq_no: string
  cli_trans_drop_route_id: string
  cli_drop_seq_no: string
  cli_trans_driver: string
  cli_trans_drop_driver: string
  cli_estimated_pick_time: string
  cli_estimated_drop_time: string
  cli_food_program: string
  cli_food_program_meal: string
  cli_schedule_meals_daily: string
  cli_schedule_meals: string[]
  cli_food_program_type: string
  food_schedule_start_date: string
  food_schedule_end_date: string
  cli_able_to_sign: string
  cli_signature_pin: string
  cli_cgs_to_sign: string
  cli_cgs_signature_pin: string
  signature_font: string
  cli_signature_name: string
  cli_dcc_status: ClientStatus
  cli_visitor_auth: string
  dis_date: string
  dis_end_date: string
  delete_shift: string
  note_title: string
  code_status: string[]
}

const INITIAL: FormState = {
  cli_name: '',
  cli_client_id: '',
  cli_first_name: '',
  cli_last_name: '',
  cli_middle_name: '',
  cli_sex: 'Female',
  cli_phone: '',
  cli_cell_phone: '',
  cli_dob: '',
  cli_adm_date: '',
  cli_email: '',
  cli_security: '',
  cli_address: '',
  cli_address2: '',
  cli_state: 'CO',
  cli_city: '',
  cli_zip: '',
  cli_material_status: '',
  cli_guardian: 'No',
  cli_guardian_name: '',
  cli_guardian_phone: '',
  cli_proxy: 'No',
  cli_proxy_name: '',
  cli_proxy_phone: '',
  cli_primary_lang: '',
  cli_secondary_lang: '',
  cli_ethenicity: '',
  cli_race: '',
  cli_religion: '',
  cli_program_name: '',
  cli_comments: '',
  cli_spouse_significant_name: '',
  cli_referral_date: '',
  cli_referral_source: '',
  ref_organization_name: '',
  ref_organization_contact_name: '',
  ref_organization_contact_email: '',
  ref_organization_contact_phone: '',
  cli_transfer_dcc_id: '',
  cli_medicare: '',
  cli_medicaid: '',
  cli_is_waiver: 'No',
  cli_waiver: [],
  cli_adv_dir_cir: 'NO',
  cli_hospital_preference: '',
  cli_add_history: '',
  cli_mltss: 'NO',
  cli_mltss_manager_name: '',
  cli_mltss_manager_no: '',
  cli_mltss_manager_email: '',
  cli_mltss_manager_ext: '',
  cli_fac_schedule_daily: 'YES',
  cli_schedule: [],
  cli_schedule_timing: 'YES',
  cli_schedule_shift: '',
  shiftstarttime: '',
  shiftendtime: '',
  cli_tra_schedule_daily: 'YES',
  cli_et_setting: 'Default Time',
  cli_tra_schedule: [],
  cli_third_party_transportation: 'NO',
  cli_third_party_transportation_reason: '',
  cli_trans_route_id: '',
  cli_pick_seq_no: '',
  cli_trans_drop_route_id: '',
  cli_drop_seq_no: '',
  cli_trans_driver: '',
  cli_trans_drop_driver: '',
  cli_estimated_pick_time: '',
  cli_estimated_drop_time: '',
  cli_food_program: 'NO',
  cli_food_program_meal: '',
  cli_schedule_meals_daily: 'YES',
  cli_schedule_meals: [],
  cli_food_program_type: '',
  food_schedule_start_date: '',
  food_schedule_end_date: '',
  cli_able_to_sign: 'YES',
  cli_signature_pin: '',
  cli_cgs_to_sign: 'NO',
  cli_cgs_signature_pin: '',
  signature_font: '',
  cli_signature_name: '',
  cli_dcc_status: 'Visitor',
  cli_visitor_auth: 'NO',
  dis_date: '',
  dis_end_date: '',
  delete_shift: 'NO',
  note_title: '',
  code_status: [],
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="ac-field">
      <span className="ac-field__label">
        {label}
        {required ? <em className="req"> *</em> : null}
      </span>
      <div className="ac-field__control">{children}</div>
      {hint ? <small className="ac-field__hint">{hint}</small> : null}
    </div>
  )
}

function YesNo({
  name,
  value,
  onChange,
  yes = 'YES',
  no = 'NO',
}: {
  name: string
  value: string
  onChange: (v: string) => void
  yes?: string
  no?: string
}) {
  return (
    <div className="choice-row">
      {[yes, no].map((opt) => (
        <label key={opt} className="choice">
          <input
            type="radio"
            name={name}
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          {opt}
        </label>
      ))}
    </div>
  )
}

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <section className={`form-section${open ? ' is-open' : ''}`}>
      <button type="button" className="form-section__head" onClick={onToggle}>
        <span>{title}</span>
        <Icon name={open ? 'chevron-left' : 'chevron-right'} />
      </button>
      {open ? <div className="form-section__body">{children}</div> : null}
    </section>
  )
}

export function AddClientPage({ currentPath, clientId }: Props) {
  const [form, setForm] = useState<FormState>(INITIAL)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    general: true,
    referral: false,
    transfer: false,
    payer: false,
    additional: false,
    schedule: false,
    transport: false,
    food: false,
    signature: false,
    status: false,
  })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(!!clientId)
  const [version, setVersion] = useState<number | undefined>()

  useEffect(() => {
    if (!clientId) return
    let mounted = true
    getClient(clientId)
      .then((client) => {
        if (!mounted) return
        const saved = client.profile && typeof client.profile === 'object' ? client.profile : {}
        setForm({
          ...INITIAL,
          ...(saved as Partial<FormState>),
          cli_name: [client.firstName, client.middleName, client.lastName].filter(Boolean).join(' '),
          cli_client_id: client.id,
          cli_first_name: client.firstName,
          cli_last_name: client.lastName,
          cli_middle_name: client.middleName || '',
          cli_phone: client.phoneHome || '',
          cli_cell_phone: client.phoneCell || '',
          cli_dob: client.dob,
          cli_adm_date: client.admissionDate || '',
          cli_email: client.email || '',
          cli_sex: client.gender || 'Female',
          cli_dcc_status: client.status,
          cli_program_name: client.program || '',
          cli_medicaid: client.medicaidId || '',
          cli_medicare: client.medicareId || '',
          cli_mltss_manager_name: client.careManager || '',
        })
        setVersion(client.version)
      })
      .catch((reason) => setError(reason instanceof ApiError ? reason.message : 'Unable to load this client.'))
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [clientId])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value }
      if (key === 'cli_first_name' || key === 'cli_last_name' || key === 'cli_middle_name') {
        const mid = (key === 'cli_middle_name' ? value : next.cli_middle_name) as string
        const first = (key === 'cli_first_name' ? value : next.cli_first_name) as string
        const last = (key === 'cli_last_name' ? value : next.cli_last_name) as string
        next.cli_name = [first, mid, last].filter(Boolean).join(' ').trim()
      }
      return next
    })
  }

  const toggleMulti = (key: MultiKey, value: string) => {
    setForm((prev) => {
      const cur = prev[key]
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]
      return { ...prev, [key]: next }
    })
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const missing = useMemo(() => {
    const req: (keyof FormState)[] = [
      'cli_first_name',
      'cli_last_name',
      'cli_sex',
      'cli_dob',
      'cli_address',
      'cli_state',
      'cli_city',
      'cli_zip',
    ]
    return req.filter((k) => !String(form[k] || '').trim())
  }, [form])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (missing.length) {
      setError('Please complete all required fields marked with *.')
      setOpenSections((s) => ({ ...s, general: true, status: true }))
      return
    }
    setSaving(true)
    setError('')
    try {
      const input = {
        clientNumber: form.cli_client_id,
        firstName: form.cli_first_name,
        lastName: form.cli_last_name,
        middleName: form.cli_middle_name,
        phoneHome: form.cli_phone,
        phoneCell: form.cli_cell_phone,
        dob: form.cli_dob,
        gender: form.cli_sex,
        status: form.cli_dcc_status,
        email: form.cli_email,
        admissionDate: form.cli_adm_date,
        address: form.cli_address,
        address2: form.cli_address2,
        city: form.cli_city,
        state: form.cli_state,
        zip: form.cli_zip,
        ssn: form.cli_security,
        program: form.cli_program_name,
        medicaidId: form.cli_medicaid,
        medicareId: form.cli_medicare,
        race: form.cli_race,
        ethnicity: form.cli_ethenicity,
        careManager: form.cli_mltss_manager_name,
        profile: { ...form },
      }
      const saved = clientId
        ? await updateClient(clientId, { ...input, version })
        : await createClient(input)
      navigate(`/clients/${saved.id}/overview`)
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to save the client.')
      setSaving(false)
    }
  }

  return (
    <AppShell
      currentPath={currentPath}
      title={clientId ? 'Edit Client' : 'Add Client'}
      subtitle={clientId ? `Update client ${clientId}` : 'Create a new client profile'}
      actions={
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/clients')}>
          <Icon name="chevron-left" /> Back to roster
        </button>
      }
    >
      <div className="container">
        {loading ? <div className="empty card"><p>Loading client…</p></div> : <form className="add-client" onSubmit={onSubmit}>
          <Section
            title="General Information"
            open={!!openSections.general}
            onToggle={() => toggleSection('general')}
          >
            <div className="form-grid">
              <Field label="Client Name" required>
                <input value={form.cli_name} readOnly placeholder="Auto from first / last name" />
              </Field>
              <Field label="Client ID #">
                <input
                  value={form.cli_client_id}
                  onInput={(e) => set('cli_client_id', (e.target as HTMLInputElement).value)}
                  placeholder="Optional external ID"
                />
              </Field>
              <Field label="First Name" required>
                <input
                  value={form.cli_first_name}
                  onInput={(e) => set('cli_first_name', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Last Name" required>
                <input
                  value={form.cli_last_name}
                  onInput={(e) => set('cli_last_name', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Middle Name">
                <input
                  value={form.cli_middle_name}
                  onInput={(e) => set('cli_middle_name', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Sex" required>
                <div className="choice-row">
                  {SEX_OPTIONS.map((opt) => (
                    <label key={opt} className="choice">
                      <input
                        type="radio"
                        name="cli_sex"
                        checked={form.cli_sex === opt}
                        onChange={() => set('cli_sex', opt)}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Home Phone">
                <input
                  value={form.cli_phone}
                  onInput={(e) => set('cli_phone', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Cell Phone">
                <input
                  value={form.cli_cell_phone}
                  onInput={(e) => set('cli_cell_phone', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Date of Birth" required>
                <input
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={form.cli_dob}
                  onInput={(e) => set('cli_dob', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Admission date">
                <input
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={form.cli_adm_date}
                  onInput={(e) => set('cli_adm_date', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Client Email ID">
                <input
                  type="email"
                  value={form.cli_email}
                  onInput={(e) => set('cli_email', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Social Security #">
                <input
                  value={form.cli_security}
                  onInput={(e) => set('cli_security', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Address1" required>
                <input
                  value={form.cli_address}
                  onInput={(e) => set('cli_address', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Address 2">
                <input
                  value={form.cli_address2}
                  onInput={(e) => set('cli_address2', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="State" required>
                <select
                  value={form.cli_state}
                  onChange={(e) => set('cli_state', (e.target as HTMLSelectElement).value)}
                >
                  {US_STATES.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="City" required>
                <input
                  value={form.cli_city}
                  onInput={(e) => set('cli_city', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Zip Code" required>
                <input
                  value={form.cli_zip}
                  onInput={(e) => set('cli_zip', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Marital Status">
                <div className="choice-row choice-row--wrap">
                  {MARITAL_OPTIONS.map((opt) => (
                    <label key={opt} className="choice">
                      <input
                        type="radio"
                        name="cli_material_status"
                        checked={form.cli_material_status === opt}
                        onChange={() => set('cli_material_status', opt)}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Primary Language">
                <input
                  value={form.cli_primary_lang}
                  onInput={(e) => set('cli_primary_lang', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Secondary Language">
                <input
                  value={form.cli_secondary_lang}
                  onInput={(e) => set('cli_secondary_lang', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Race">
                <select
                  value={form.cli_ethenicity}
                  onChange={(e) => set('cli_ethenicity', (e.target as HTMLSelectElement).value)}
                >
                  <option value="">Select…</option>
                  {RACE_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Ethnicity">
                <select
                  value={form.cli_race}
                  onChange={(e) => set('cli_race', (e.target as HTMLSelectElement).value)}
                >
                  <option value="">Select…</option>
                  {ETHNICITY_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Religion">
                <input
                  value={form.cli_religion}
                  onInput={(e) => set('cli_religion', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Program Name">
                <input
                  value={form.cli_program_name}
                  onInput={(e) => set('cli_program_name', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Spouse’s / significant other’s name">
                <input
                  value={form.cli_spouse_significant_name}
                  onInput={(e) =>
                    set('cli_spouse_significant_name', (e.target as HTMLInputElement).value)
                  }
                />
              </Field>
            </div>

            <div className="form-grid form-grid--full">
              <Field label="Code Status">
                <div className="choice-row choice-row--wrap">
                  {CODE_STATUS_OPTIONS.map((opt) => (
                    <label key={opt} className="choice">
                      <input
                        type="checkbox"
                        checked={form.code_status.includes(opt)}
                        onChange={() => toggleMulti('code_status', opt)}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Comments">
                <textarea
                  rows={3}
                  value={form.cli_comments}
                  onInput={(e) => set('cli_comments', (e.target as HTMLTextAreaElement).value)}
                />
              </Field>
            </div>

            <div className="form-grid">
              <Field label="Guardian">
                <YesNo
                  name="cli_guardian"
                  value={form.cli_guardian}
                  onChange={(v) => set('cli_guardian', v)}
                  yes="Yes"
                  no="No"
                />
              </Field>
              {form.cli_guardian === 'Yes' ? (
                <>
                  <Field label="Guardian Name">
                    <input
                      value={form.cli_guardian_name}
                      onInput={(e) =>
                        set('cli_guardian_name', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                  <Field label="Guardian Phone">
                    <input
                      value={form.cli_guardian_phone}
                      onInput={(e) =>
                        set('cli_guardian_phone', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                </>
              ) : null}
              <Field label="Proxy">
                <YesNo
                  name="cli_proxy"
                  value={form.cli_proxy}
                  onChange={(v) => set('cli_proxy', v)}
                  yes="Yes"
                  no="No"
                />
              </Field>
              {form.cli_proxy === 'Yes' ? (
                <>
                  <Field label="Proxy Name">
                    <input
                      value={form.cli_proxy_name}
                      onInput={(e) => set('cli_proxy_name', (e.target as HTMLInputElement).value)}
                    />
                  </Field>
                  <Field label="Proxy Phone">
                    <input
                      value={form.cli_proxy_phone}
                      onInput={(e) => set('cli_proxy_phone', (e.target as HTMLInputElement).value)}
                    />
                  </Field>
                </>
              ) : null}
            </div>
          </Section>

          <Section
            title="Referral Details"
            open={!!openSections.referral}
            onToggle={() => toggleSection('referral')}
          >
            <div className="form-grid">
              <Field label="Referral Date">
                <input
                  placeholder="MM/DD/YYYY"
                  value={form.cli_referral_date}
                  onInput={(e) => set('cli_referral_date', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Referral Source">
                <input
                  value={form.cli_referral_source}
                  onInput={(e) => set('cli_referral_source', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Referral Organization">
                <input
                  value={form.ref_organization_name}
                  onInput={(e) =>
                    set('ref_organization_name', (e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <Field label="Referral Primary Contact">
                <input
                  value={form.ref_organization_contact_name}
                  onInput={(e) =>
                    set('ref_organization_contact_name', (e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <Field label="Primary Contact Email">
                <input
                  value={form.ref_organization_contact_email}
                  onInput={(e) =>
                    set('ref_organization_contact_email', (e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <Field label="Primary Contact Phone">
                <input
                  value={form.ref_organization_contact_phone}
                  onInput={(e) =>
                    set('ref_organization_contact_phone', (e.target as HTMLInputElement).value)
                  }
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Transfer Information"
            open={!!openSections.transfer}
            onToggle={() => toggleSection('transfer')}
          >
            <div className="form-grid form-grid--full">
              <Field label="From Day Care Center">
                <textarea
                  rows={3}
                  value={form.cli_transfer_dcc_id}
                  onInput={(e) =>
                    set('cli_transfer_dcc_id', (e.target as HTMLTextAreaElement).value)
                  }
                  placeholder="Previous day care center"
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Payer (Insurance) Details"
            open={!!openSections.payer}
            onToggle={() => toggleSection('payer')}
          >
            <div className="form-grid">
              <Field label="Medicare #">
                <input
                  value={form.cli_medicare}
                  onInput={(e) => set('cli_medicare', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Medicaid ID#">
                <input
                  value={form.cli_medicaid}
                  onInput={(e) => set('cli_medicaid', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Waiver">
                <YesNo
                  name="cli_is_waiver"
                  value={form.cli_is_waiver}
                  onChange={(v) => set('cli_is_waiver', v)}
                  yes="Yes"
                  no="No"
                />
              </Field>
            </div>
            {form.cli_is_waiver === 'Yes' ? (
              <div className="form-grid form-grid--full">
                <Field label="Waiver programs">
                  <div className="choice-row choice-row--wrap">
                    {WAIVER_OPTIONS.map((opt) => (
                      <label key={opt} className="choice">
                        <input
                          type="checkbox"
                          checked={form.cli_waiver.includes(opt)}
                          onChange={() => toggleMulti('cli_waiver', opt)}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </Field>
              </div>
            ) : null}
          </Section>

          <Section
            title="Additional Information"
            open={!!openSections.additional}
            onToggle={() => toggleSection('additional')}
          >
            <div className="form-grid">
              <Field
                label="Allergies"
                hint="Data comes from Nursing Tab → Client Allergies (read-only in MyADC)."
              >
                <input value="" readOnly placeholder="Allergies" />
              </Field>
              <Field label="Advance Directives Circulated">
                <YesNo
                  name="cli_adv_dir_cir"
                  value={form.cli_adv_dir_cir}
                  onChange={(v) => set('cli_adv_dir_cir', v)}
                />
              </Field>
              <Field label="Hospital Preference">
                <input
                  value={form.cli_hospital_preference}
                  onInput={(e) =>
                    set('cli_hospital_preference', (e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <Field label="Diagnosis / billable diagnosis">
                <input
                  placeholder="Select in MyADC after diagnoses are configured"
                  readOnly
                  value=""
                />
              </Field>
            </div>
            <div className="form-grid form-grid--full">
              <Field label="Additional History">
                <textarea
                  rows={3}
                  value={form.cli_add_history}
                  onInput={(e) => set('cli_add_history', (e.target as HTMLTextAreaElement).value)}
                />
              </Field>
            </div>
            <div className="form-grid">
              <Field label="MLTSS">
                <YesNo
                  name="cli_mltss"
                  value={form.cli_mltss}
                  onChange={(v) => set('cli_mltss', v)}
                />
              </Field>
              {form.cli_mltss === 'YES' ? (
                <>
                  <Field label="Case Manager Name">
                    <input
                      value={form.cli_mltss_manager_name}
                      onInput={(e) =>
                        set('cli_mltss_manager_name', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                  <Field label="Case Manager Phone No.">
                    <input
                      value={form.cli_mltss_manager_no}
                      onInput={(e) =>
                        set('cli_mltss_manager_no', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                  <Field label="Ext.">
                    <input
                      value={form.cli_mltss_manager_ext}
                      onInput={(e) =>
                        set('cli_mltss_manager_ext', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                  <Field label="Case Manager Email ID">
                    <input
                      value={form.cli_mltss_manager_email}
                      onInput={(e) =>
                        set('cli_mltss_manager_email', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                </>
              ) : null}
            </div>
          </Section>

          <Section
            title="Schedule Details (Attendance Schedule)"
            open={!!openSections.schedule}
            onToggle={() => toggleSection('schedule')}
          >
            <div className="form-grid">
              <Field label="Daily Schedule">
                <YesNo
                  name="cli_fac_schedule_daily"
                  value={form.cli_fac_schedule_daily}
                  onChange={(v) => set('cli_fac_schedule_daily', v)}
                />
              </Field>
              <Field label="Same Shift Timing Every Day">
                <YesNo
                  name="cli_schedule_timing"
                  value={form.cli_schedule_timing}
                  onChange={(v) => set('cli_schedule_timing', v)}
                />
              </Field>
              <Field label="Schedule (days)">
                <div className="choice-row choice-row--wrap">
                  {WEEKDAYS.map((d) => (
                    <label key={d} className="choice">
                      <input
                        type="checkbox"
                        checked={form.cli_schedule.includes(d)}
                        onChange={() => toggleMulti('cli_schedule', d)}
                      />
                      {WEEKDAY_LABELS[d] || d}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Shift">
                <select
                  value={form.cli_schedule_shift}
                  onChange={(e) =>
                    set('cli_schedule_shift', (e.target as HTMLSelectElement).value)
                  }
                >
                  {SHIFT_OPTIONS.map((o) => (
                    <option key={o.value || 'empty'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Shift Start Time">
                <input
                  placeholder="HH:MM"
                  value={form.shiftstarttime}
                  onInput={(e) => set('shiftstarttime', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Shift End Time">
                <input
                  placeholder="HH:MM"
                  value={form.shiftendtime}
                  onInput={(e) => set('shiftendtime', (e.target as HTMLInputElement).value)}
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Transportation Schedule"
            open={!!openSections.transport}
            onToggle={() => toggleSection('transport')}
          >
            <div className="form-grid">
              <Field label="Daily Transportation Schedule">
                <YesNo
                  name="cli_tra_schedule_daily"
                  value={form.cli_tra_schedule_daily}
                  onChange={(v) => set('cli_tra_schedule_daily', v)}
                />
              </Field>
              <Field label="Estimated Time Setting">
                <div className="choice-row choice-row--wrap">
                  {ET_SETTING_OPTIONS.map((opt) => (
                    <label key={opt} className="choice">
                      <input
                        type="radio"
                        name="cli_et_setting"
                        checked={form.cli_et_setting === opt}
                        onChange={() => set('cli_et_setting', opt)}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Transportation days">
                <div className="choice-row choice-row--wrap">
                  {WEEKDAYS.map((d) => (
                    <label key={d} className="choice">
                      <input
                        type="checkbox"
                        checked={form.cli_tra_schedule.includes(d)}
                        onChange={() => toggleMulti('cli_tra_schedule', d)}
                      />
                      {WEEKDAY_LABELS[d] || d}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Third Party / NEMT">
                <YesNo
                  name="cli_third_party_transportation"
                  value={form.cli_third_party_transportation}
                  onChange={(v) => set('cli_third_party_transportation', v)}
                />
              </Field>
              {form.cli_third_party_transportation === 'YES' ? (
                <Field label="NEMT Broker">
                  <input
                    value={form.cli_third_party_transportation_reason}
                    onInput={(e) =>
                      set(
                        'cli_third_party_transportation_reason',
                        (e.target as HTMLInputElement).value,
                      )
                    }
                    placeholder="Broker name / reason"
                  />
                </Field>
              ) : null}
              <Field label="Pick-up Route">
                <select
                  value={form.cli_trans_route_id}
                  onChange={(e) =>
                    set('cli_trans_route_id', (e.target as HTMLSelectElement).value)
                  }
                >
                  {ROUTE_OPTIONS.map((o) => (
                    <option key={o.value || 'empty'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Pick-up Seq #">
                <input
                  value={form.cli_pick_seq_no}
                  onInput={(e) => set('cli_pick_seq_no', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Drop-off Route">
                <select
                  value={form.cli_trans_drop_route_id}
                  onChange={(e) =>
                    set('cli_trans_drop_route_id', (e.target as HTMLSelectElement).value)
                  }
                >
                  {ROUTE_OPTIONS.map((o) => (
                    <option key={`drop-${o.value || 'empty'}`} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Drop-off Seq #">
                <input
                  value={form.cli_drop_seq_no}
                  onInput={(e) => set('cli_drop_seq_no', (e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Pick-up Driver">
                <select
                  value={form.cli_trans_driver}
                  onChange={(e) =>
                    set('cli_trans_driver', (e.target as HTMLSelectElement).value)
                  }
                >
                  {DRIVER_OPTIONS.map((o) => (
                    <option key={o.value || 'empty'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Drop-off Driver">
                <select
                  value={form.cli_trans_drop_driver}
                  onChange={(e) =>
                    set('cli_trans_drop_driver', (e.target as HTMLSelectElement).value)
                  }
                >
                  {DRIVER_OPTIONS.map((o) => (
                    <option key={`drop-${o.value || 'empty'}`} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Estimated Pick-up Time">
                <input
                  placeholder="HH:MM"
                  value={form.cli_estimated_pick_time}
                  onInput={(e) =>
                    set('cli_estimated_pick_time', (e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <Field label="Estimated Drop-off Time">
                <input
                  placeholder="HH:MM"
                  value={form.cli_estimated_drop_time}
                  onInput={(e) =>
                    set('cli_estimated_drop_time', (e.target as HTMLInputElement).value)
                  }
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Food Program Enrollment"
            open={!!openSections.food}
            onToggle={() => toggleSection('food')}
          >
            <div className="form-grid">
              <Field label="Food Program">
                <YesNo
                  name="cli_food_program"
                  value={form.cli_food_program}
                  onChange={(v) => set('cli_food_program', v)}
                />
              </Field>
              {form.cli_food_program === 'YES' ? (
                <>
                  <Field label="Diet Plan">
                    <select
                      value={form.cli_food_program_meal}
                      onChange={(e) =>
                        set('cli_food_program_meal', (e.target as HTMLSelectElement).value)
                      }
                    >
                      <option value="">All ( Diet Plan Name )</option>
                    </select>
                  </Field>
                  <Field label="Daily Meals">
                    <YesNo
                      name="cli_schedule_meals_daily"
                      value={form.cli_schedule_meals_daily}
                      onChange={(v) => set('cli_schedule_meals_daily', v)}
                    />
                  </Field>
                  <Field label="Meals">
                    <div className="choice-row choice-row--wrap">
                      {MEAL_OPTIONS.map((opt) => (
                        <label key={opt.value} className="choice">
                          <input
                            type="checkbox"
                            checked={form.cli_schedule_meals.includes(opt.value)}
                            onChange={() => toggleMulti('cli_schedule_meals', opt.value)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </Field>
                  <Field label="Determination">
                    <div className="choice-row choice-row--wrap">
                      {FOOD_PROGRAM_TYPE_OPTIONS.map((opt) => (
                        <label key={opt} className="choice">
                          <input
                            type="radio"
                            name="cli_food_program_type"
                            checked={form.cli_food_program_type === opt}
                            onChange={() => set('cli_food_program_type', opt)}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </Field>
                  <Field label="Food Schedule Start Date">
                    <input
                      placeholder="MM/DD/YYYY"
                      value={form.food_schedule_start_date}
                      onInput={(e) =>
                        set('food_schedule_start_date', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                  <Field label="Food Schedule End Date">
                    <input
                      placeholder="MM/DD/YYYY"
                      value={form.food_schedule_end_date}
                      onInput={(e) =>
                        set('food_schedule_end_date', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                </>
              ) : null}
            </div>
          </Section>

          <Section
            title="Signature Details"
            open={!!openSections.signature}
            onToggle={() => toggleSection('signature')}
          >
            <div className="form-grid">
              <Field label="Able to Sign">
                <YesNo
                  name="cli_able_to_sign"
                  value={form.cli_able_to_sign}
                  onChange={(v) => set('cli_able_to_sign', v)}
                />
              </Field>
              {form.cli_able_to_sign === 'YES' ? (
                <>
                  <Field label="Signature PIN">
                    <input
                      value={form.cli_signature_pin}
                      onInput={(e) =>
                        set('cli_signature_pin', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                  <Field label="Signature Font">
                    <select
                      value={form.signature_font}
                      onChange={(e) =>
                        set('signature_font', (e.target as HTMLSelectElement).value)
                      }
                    >
                      {SIGNATURE_FONT_OPTIONS.map((o) => (
                        <option key={o.value || 'empty'} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Signature Name">
                    <input
                      value={form.cli_signature_name}
                      onInput={(e) =>
                        set('cli_signature_name', (e.target as HTMLInputElement).value)
                      }
                    />
                  </Field>
                </>
              ) : null}
              <Field label="Caregiver Able to Sign">
                <YesNo
                  name="cli_cgs_to_sign"
                  value={form.cli_cgs_to_sign}
                  onChange={(v) => set('cli_cgs_to_sign', v)}
                />
              </Field>
              {form.cli_cgs_to_sign === 'YES' ? (
                <Field label="Caregiver Signature PIN">
                  <input
                    value={form.cli_cgs_signature_pin}
                    onInput={(e) =>
                      set('cli_cgs_signature_pin', (e.target as HTMLInputElement).value)
                    }
                  />
                </Field>
              ) : null}
            </div>
          </Section>

          <Section
            title="Status"
            open={!!openSections.status}
            onToggle={() => toggleSection('status')}
          >
            <div className="form-grid">
              <Field label="DCC Status" required>
                <div className="choice-row choice-row--wrap">
                  {STATUS_OPTIONS.map((opt) => (
                    <label key={opt} className="choice">
                      <input
                        type="radio"
                        name="cli_dcc_status"
                        checked={form.cli_dcc_status === opt}
                        onChange={() => set('cli_dcc_status', opt)}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Visitor Authorization">
                <YesNo
                  name="cli_visitor_auth"
                  value={form.cli_visitor_auth}
                  onChange={(v) => set('cli_visitor_auth', v)}
                />
              </Field>
              {(form.cli_dcc_status === 'Discharged' || form.cli_dcc_status === 'Hold') && (
                <>
                  <Field label="Start / Discharge Date">
                    <input
                      placeholder="MM/DD/YYYY"
                      value={form.dis_date}
                      onInput={(e) => set('dis_date', (e.target as HTMLInputElement).value)}
                    />
                  </Field>
                  <Field label="End Date">
                    <input
                      placeholder="MM/DD/YYYY"
                      value={form.dis_end_date}
                      onInput={(e) => set('dis_end_date', (e.target as HTMLInputElement).value)}
                    />
                  </Field>
                  <Field label="Delete Shifts">
                    <YesNo
                      name="delete_shift"
                      value={form.delete_shift}
                      onChange={(v) => set('delete_shift', v)}
                    />
                  </Field>
                  {form.cli_dcc_status === 'Discharged' ? (
                    <Field label="Note Title">
                      <select
                        value={form.note_title}
                        onChange={(e) =>
                          set('note_title', (e.target as HTMLSelectElement).value)
                        }
                      >
                        <option value="">Select…</option>
                        {DISCHARGE_NOTE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : null}
                </>
              )}
            </div>
          </Section>

          {error ? <p className="form-error">{error}</p> : null}

          <div className="add-client__foot">
            <button type="button" className="btn btn--ghost" onClick={() => navigate('/clients')}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={saving}>
              <Icon name={clientId ? 'edit' : 'plus'} /> {saving ? 'Saving…' : clientId ? 'Save changes' : 'Save client'}
            </button>
          </div>
        </form>}
      </div>
    </AppShell>
  )
}
